import { useState } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'
import {
  ExtendableMutationOptions,
  MutatedResponse
} from 'shared/api/api.types'
import { onLoginSuccess } from 'shared/api/auth/utils/onLoginSuccess'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { SessionStorageKey } from 'shared/constants/sessionStorage'
import { logger } from 'shared/services/logger'

import { authApi } from '../..'
import { OtpErrorData } from '../../auth.types'
import { onLoginError } from '../../utils/onLoginError'
import { LoginRequest, LoginResponse } from './login.types'

export const login = async (data: LoginRequest) => {
  const response = await authApi.post<AxiosResponse<LoginResponse>>(
    'login',
    data,
    // Login has no access token to refresh against, so a wrong-password 401
    // must not trigger the shared axios instance's refresh-and-retry
    // interceptor, which would silently resend this request a second time.
    { _retry: true } as never
  )

  return response.data.data
}

export type LoginMutatedResponse = MutatedResponse<typeof login>

export const useLoginMutation = (
  options?: ExtendableMutationOptions<LoginMutatedResponse, LoginRequest>
) => {
  const queryClient = useQueryClient()
  const [isOtpLogin, setIsOtpLogin] = useState(false)
  return useMutation({
    mutationFn: data => {
      logger.track('auth.login_started', {
        method: 'otp' in data ? 'otp' : 'password',
        rememberMe: data.remember_me
      })
      if ('otp' in data) {
        setIsOtpLogin(true)
      } else {
        setIsOtpLogin(false)
      }
      return login(data)
    },
    ...options,
    onMutate: (data, ...args) => {
      options?.onMutate?.(data, ...args)
    },
    onSuccess: (data, ...args) => {
      logger.track('auth.login_completed', { success: true })
      onLoginSuccess(data, queryClient, isOtpLogin)
      options?.onSuccess?.(data, ...args)
    },
    onError: (error, data, ...args) => {
      const axiosError = error as AxiosError<OtpErrorData>
      logger.track('auth.login_completed', {
        success: false,
        errorCode: axiosError.response?.status
      })
      onLoginError(error as AxiosError<OtpErrorData>)

      sessionStorage.setItem(SessionStorageKey.OTP_LOGGING_IN_EMAIL, data.email)
      sessionStorage.setItem(
        SessionStorageKey.OTP_LOGGING_IN_PASSWORD,
        data.password
      )
      if (FEATURE_FLAGS.SHOW_REMEMBER_ME) {
        sessionStorage.setItem(
          SessionStorageKey.OTP_LOGGING_IN_REMEMBER_ME,
          data.remember_me.toString()
        )
      }

      options?.onError?.(error, data, ...args)
    }
  })
}
